/**
 * 用户相关工具函数
 * 提供统一的用户信息获取方式
 * 
 * @deprecated 推荐使用 CurrentUserContext (app/src/contexts/CurrentUserContext.tsx) / useCurrentUser 管理用户缓存与行为
 * 这些工具函数保留作为向后兼容的包装器，主要用于：
 * 1. Login 等需在 Hook 外读取缓存的场景
 * 2. 纯函数工具场景（不需要 React Context）
 * 
 * 在 React 组件中，优先使用 useCurrentUser() Hook
 */
import { UserCacheData, UserInfo, OrgInfo, DepartmentUserInfo } from '../types/auth';
import { resolveUserAppConfig } from './userAppConfig';

/**
 * 缓存键名常量
 */
export const USER_CACHE_KEYS = {
  USER_CACHE: 'userCache'
};

/**
 * 获取完整的用户缓存数据
 * @returns 用户缓存数据，如果不存在或解析失败则返回 null
 */
export function getUserCacheData(): UserCacheData | null {
  try {
    const cacheStr = localStorage.getItem(USER_CACHE_KEYS.USER_CACHE);
    if (!cacheStr) {
      return null;
    }
    return JSON.parse(cacheStr) as UserCacheData;
  } catch (error) {
    console.error('解析用户缓存数据失败:', error);
    return null;
  }
}

/**
 * 获取当前用户信息
 * @returns 用户信息对象
 * @throws Error 如果没有找到用户信息
 */
export function getCurrentUserInfo(): UserInfo {
  const cacheData = getUserCacheData();
  if (!cacheData?.user) {
    throw new Error('未找到用户信息，请重新登录');
  }
  return cacheData.user;
}

/**
 * 获取当前用户ID
 * @returns 用户ID
 * @throws Error 如果没有找到用户信息
 */
export function getCurrentUserID(): string {
  const userInfo = getCurrentUserInfo();
  return userInfo.userID;
}

/**
 * 获取当前用户的组织ID
 * @returns 组织ID，如果用户没有关联组织则返回 null
 * @throws Error 如果没有找到用户信息
 */
export function getCurrentUserOrgID(): string | null {
  const userInfo = getCurrentUserInfo();
  return userInfo.userOrgID || null;
}

/**
 * 获取当前用户的组织信息
 * @returns 组织信息，如果用户没有关联组织则返回 null
 * @throws Error 如果没有找到用户信息
 */
export function getCurrentUserOrganization(): OrgInfo | null {
  const cacheData = getUserCacheData();
  if (!cacheData) {
    throw new Error('未找到用户信息，请重新登录');
  }
  return cacheData.organization || null;
}

/**
 * 获取当前用户的部门信息列表
 * @returns 用户关联的部门信息数组
 * @throws Error 如果没有找到用户信息
 */
export function getCurrentUserDepartments(): DepartmentUserInfo[] {
  const cacheData = getUserCacheData();
  if (!cacheData) {
    throw new Error('未找到用户信息，请重新登录');
  }
  return cacheData.departments || [];
}

/**
 * 获取用户在指定部门的职位信息
 * @param departmentID 部门ID
 * @returns 用户在该部门的职位，如果没有关联则返回 null
 */
export function getUserPositionInDepartment(departmentID: string): string | null {
  const departments = getCurrentUserDepartments();
  const department = departments.find(dept => dept.departmentID === departmentID);
  return department?.userPositionInDepartment || null;
}

/**
 * 检查用户是否属于指定部门
 * @param departmentID 部门ID
 * @returns 是否属于该部门
 */
export function isUserInDepartment(departmentID: string): boolean {
  const departments = getCurrentUserDepartments();
  return departments.some(dept => dept.departmentID === departmentID);
}

/**
 * 获取当前用户的访问令牌
 * @returns JWT 访问令牌
 * @throws Error 如果没有找到访问令牌
 */
export function getAccessToken(): string {
  const cacheData = getUserCacheData();
  if (!cacheData?.accessToken) {
    throw new Error('未找到访问令牌，请重新登录');
  }
  return cacheData.accessToken;
}

/**
 * 存储用户缓存数据
 * @param userData 完整的用户缓存数据
 */
export function setUserCacheData(userData: UserCacheData): void {
  try {
    localStorage.setItem(USER_CACHE_KEYS.USER_CACHE, JSON.stringify(userData));
    localStorage.setItem('access_token', userData.accessToken);
  } catch (error) {
    console.error('存储用户缓存数据失败:', error);
    throw new Error('存储用户数据失败');
  }
}

/**
 * 清空所有用户相关缓存
 */
export function clearUserCache(): void {
  try {
    // 清除新的缓存结构
    localStorage.removeItem(USER_CACHE_KEYS.USER_CACHE);
    
    // 兼容清除旧的缓存结构
    localStorage.removeItem('user');
    localStorage.removeItem('access_token');
  } catch (error) {
    console.error('清空用户缓存失败:', error);
  }
}

/**
 * 检查用户是否已登录
 * @returns 是否已登录
 */
export function isUserLoggedIn(): boolean {
  const cacheData = getUserCacheData();
  return !!(cacheData?.user && cacheData?.accessToken);
}

/**
 * 检查缓存是否过期（可选功能，目前暂未实现过期逻辑）
 * @returns 是否已过期
 */
export function isCacheExpired(): boolean {
  // TODO: 实现缓存过期检查逻辑
  return false;
}

/**
 * 获取用户角色
 * @returns 用户角色
 */
export function getCurrentUserRole(): string | undefined {
  try {
    const userInfo = getCurrentUserInfo();
    return userInfo.userRole;
  } catch {
    return undefined;
  }
}

/**
 * 获取用户账户类型
 * @returns 用户账户类型
 */
export function getCurrentUserAccountType(): string | undefined {
  try {
    const userInfo = getCurrentUserInfo();
    return userInfo.userAccountType;
  } catch {
    return undefined;
  }
}

/**
 * 获取用户订阅信息 - 分场排期产品
 * @returns 订阅信息对象
 */
export function getUserBreakdownSubscription() {
  try {
    const userInfo = getCurrentUserInfo();
    return {
      version: userInfo.userSubscriptionBreakdownVersion,
      startDate: userInfo.userSubscriptionBreakdownStartDate,
      expirationDate: userInfo.userSubscriptionBreakdownExpirationDate
    };
  } catch {
    return null;
  }
}

/**
 * 获取用户订阅信息 - 预算管理产品
 * @returns 订阅信息对象
 */
export function getUserBudgetsSubscription() {
  try {
    const userInfo = getCurrentUserInfo();
    return {
      version: userInfo.userSubscriptionBudgetsVersion,
      startDate: userInfo.userSubscriptionBudgetsStartDate,
      expirationDate: userInfo.userSubscriptionBudgetsExpirationDate
    };
  } catch {
    return null;
  }
}

/**
 * 获取用户管理的组织ID列表
 * @returns 组织ID数组
 */
export function getUserManagementOrgs(): string[] {
  try {
    const userInfo = getCurrentUserInfo();
    if (!userInfo.userManagementOrgs) {
      return [];
    }
    return userInfo.userManagementOrgs.split(',').filter(id => id.trim());
  } catch {
    return [];
  }
}

/**
 * 获取用户管理的部门ID列表
 * @returns 部门ID数组
 */
export function getUserManagementDepartments(): string[] {
  try {
    const userInfo = getCurrentUserInfo();
    if (!userInfo.userManagementDepartments) {
      return [];
    }
    return userInfo.userManagementDepartments.split(',').filter(id => id.trim());
  } catch {
    return [];
  }
}

/**
 * 获取用户配置信息
 * @returns 用户配置对象
 */
export function getUserConfig() {
  try {
    const userInfo = getCurrentUserInfo();
    return resolveUserAppConfig(userInfo.userAppConfig, 'zh-CN');
  } catch {
    return null;
  }
}

/**
 * 获取用户行为记忆信息
 * @returns 用户行为记忆对象
 */
export function getUserMemory() {
  try {
    const userInfo = getCurrentUserInfo();
    return {
      lastUsedFilmID: userInfo.userLastUsedFilmID,
      lastUsedBudgetID: userInfo.userLastUsedBudgetID,
      lastUsedBudgetItemID: userInfo.userLastUsedBudgetItemID,
      lastUsedBreakdownID: userInfo.userLastUsedBreakdownID,
      lastUsedSceneID: userInfo.userLastUsedSceneID,
      lastUsedElementID: userInfo.userLastUsedElementID,
      lastUsedScheduleID: userInfo.userLastUsedScheduleID,
      lastUsedSlotID: userInfo.userLastUsedSlotID,
      lastUsedCalDateID: userInfo.userLastUsedCalDateID
    };
  } catch {
    return null;
  }
}

/**
 * 获取用户角色列表（支持多选）
 * @returns 角色数组
 */
export function getUserRoles(): string[] {
  try {
    const userInfo = getCurrentUserInfo();
    if (!userInfo.userRole) {
      return [];
    }
    return userInfo.userRole.split(',').map(role => role.trim()).filter(role => role);
  } catch {
    return [];
  }
}

/**
 * 检查用户是否具有指定角色
 * @param role 角色名称
 * @returns 是否具有该角色
 */
export function hasUserRole(role: string): boolean {
  const roles = getUserRoles();
  return roles.includes(role);
}

/**
 * 获取用户组织ID列表（支持多选）
 * @returns 组织ID数组
 */
export function getUserOrgIDs(): string[] {
  try {
    const userInfo = getCurrentUserInfo();
    if (!userInfo.userOrgID) {
      return [];
    }
    return userInfo.userOrgID.split(',').filter(id => id.trim());
  } catch {
    return [];
  }
}